import React, { useState } from 'react';
import styles from './styles/StepPlans.module.css';
import sharedStyles from './styles/BrandSignup.module.css';

const BUTTON_TEXT = {
  basic: 'Select Plan',
  search: 'Get Search',
  premium: 'Get Concierge'
};

const StepPlans = ({ brandSignup, onComplete, onBack, canGoBack, loading }) => {
  const [billing, setBilling] = useState('monthly');
  const [selectedTier, setSelectedTier] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSelectPlan = (tier) => {
    if (isProcessing) return;

    setIsProcessing(true);
    setSelectedTier(tier);
    onComplete({
      selected_tier: tier,
      billing_cadence: billing
    });
  };

  const renderPlanCard = (plan) => {
    const { tier, title, description, price, subtitle, features, bestFor, isFeatured = false } = plan;
    const buttonText = BUTTON_TEXT[tier];

    return (
      <div
        key={tier}
        className={`${styles.pricingCard} ${isFeatured ? styles.featured : ''}`}
      >
        {isFeatured && (
          <div className={styles.featuredLabel}>
            Most Popular
          </div>
        )}
        <h3>{title}</h3>
        <p className={styles.description}>{description}</p>
        <p className={`${styles.price} dm-serif`}>
          {price}
        </p>
        <p className={styles.subtitle}>{subtitle}</p>
        <button
          className={`${styles.button} ${isFeatured ? styles.primaryButton : styles.infoButton}`}
          onClick={(e) => {
            e.stopPropagation();
            handleSelectPlan(tier);
          }}
          disabled={loading || isProcessing}
        >
          {isProcessing && selectedTier === tier ? 'Processing...' : buttonText}
        </button>
        {bestFor && (
          <>
            <hr className={styles.divider} />
            <p className={styles.bestForHeader}>BEST FOR</p>
            <p className={styles.bestForBody}>{bestFor}</p>
          </>
        )}
        <hr className={styles.divider} />
        <p className={styles.standoutFeatures}>{features.header}</p>
        <ul className={styles.featureList}>
          {features.items.map((feature, index) => (
            <li key={index}>
              <strong>{feature.title}</strong><br />
              <span className={styles.featureDescription}>{feature.description}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const factoryMatchingFeatures = {
    header: 'WHAT\'S INCLUDED',
    items: [
      {
        title: 'Unlimited RFQs',
        description: 'Submit unlimited requests for products, materials, packaging, or samples.'
      },
      {
        title: 'Unlimited Factory Matches',
        description: 'We analyze your RFQ line by line—quantity, location, capabilities, certifications, and more - then instantly match you with factories that specialize in your product type and meet your unique requirements.'
      },
      {
        title: 'Priority Badge',
        description: 'Your projects feature a priority badge and appear first in the Factory Introduction Center.'
      }
    ]
  };

  const searchFeatures = {
    header: 'EVERYTHING IN FACTORY MATCHING, PLUS:',
    items: [
      {
        title: 'Unlimited RFQs',
        description: 'Submit unlimited requests for products, materials, packaging, or samples.'
      },
      {
        title: 'Warm Factory Introductions',
        description: 'Get personal introductions to factory partners so they prioritize your project and give you favorable terms.'
      },
      {
        title: 'Full Search Access to 5,000+ Vetted Factories',
        description: 'Search, filter, and message our entire network of factories directly.'
      },
      {
        title: 'Factory Matches',
        description: 'Our system reads every detail of your RFQ and suggests the factories that specialize in your exact needs.'
      },
      {
        title: 'Priority Badge',
        description: 'Your projects feature a priority badge and appear first in the Factory Introduction Center.'
      }
    ]
  };

  const conciergeFeatures = {
    header: 'EVERYTHING IN SEARCH, PLUS:',
    items: [
      {
        title: 'Sourcing Coordination',
        description: 'We\'ll reach out to factories on your behalf, coordinate quoting, and present recommended options for your project.'
      },
      {
        title: 'Quoting & Negotiation Help',
        description: 'We\'ll advise you on pricing, negotiation, minimum order quantities and more.'
      },
      {
        title: 'Ask Questions Anytime',
        description: 'Help when you need it via a private WhatsApp chat or email.'
      },
      {
        title: 'Live Sourcing Support',
        description: 'Up to 1 hour with a Nearshore Sourcing Expert.'
      },
      {
        title: 'Money Back Guarantee',
        description: 'Get a full refund if we don\'t cover the product category.'
      }
    ]
  };

  const buildPlans = (cadence) => {
    const annual = cadence === 'annual';
    const priceBlock = (amount) => (
      <><div>{amount}</div><div className={styles.priceUnit}>{annual ? 'USD /year' : 'USD /month'}</div></>
    );

    return [
      {
        tier: 'basic',
        title: 'Factory Matching',
        description: 'Submit RFQs and get instant factory matches.',
        price: priceBlock(annual ? '$375' : '$39'),
        subtitle: annual ? 'Or $39 /month' : 'Or $375 /annual (SAVE 20%)',
        bestFor: 'Brands who want fast, direct connections to vetted factories.',
        features: factoryMatchingFeatures
      },
      {
        tier: 'search',
        title: 'Search',
        description: 'Full access to our factory network with warm introductions and direct outreach.',
        price: priceBlock(annual ? '$1,900' : '$199'),
        subtitle: annual ? 'Or $199 /month' : 'Or $1,900 /annual (SAVE 20%)',
        isFeatured: true,
        bestFor: 'Brands who want to explore the full factory network and connect directly.',
        features: searchFeatures
      },
      {
        tier: 'premium',
        title: 'Concierge + Search',
        description: 'Factory introductions, full search access, and expert nearshoring guidance.',
        price: priceBlock(annual ? '$4,750' : '$499'),
        subtitle: annual ? 'Or $499 /month' : 'Or $4,750 /annual (SAVE 20%)',
        bestFor: 'Brands who want hands-on sourcing help and direct intros.',
        features: conciergeFeatures
      }
    ];
  };

  const plans = buildPlans(billing);

  return (
    <div className={styles.stepContainer}>
      <div className={styles.header}>
        <h1 className={styles.title}>Choose Your Plan</h1>
        <p className={styles.subtitle}>Select the plan that best fits your sourcing needs</p>
      </div>

      <div className={styles.pricingToggle}>
        <button
          className={`${styles.toggleButton} ${billing === 'monthly' ? styles.active : ''}`}
          onClick={() => setBilling('monthly')}
        >
          Monthly
        </button>
        <button
          className={`${styles.toggleButton} ${billing === 'annual' ? styles.active : ''}`}
          onClick={() => setBilling('annual')}
        >
          Annual
        </button>
      </div>

      <div className={styles.pricingGrid}>
        {plans.map(plan => renderPlanCard(plan))}
      </div>
    </div>
  );
};

export default StepPlans;
